// @ts-strict-ignore
import {nanoid} from 'nanoid';

import {CustomProperty} from 'common/types/reports/query';
import {getOwnAttr} from 'common/util';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ProjectDetails} from 'common/report/stores/project-details';
import {ProjectFeatureFlag} from 'common/report/constants';
import {Property} from 'common/query-builder/query-block/properties-menu/types';

export const INLINE_CUSTOM_PROPERTY_ID_PREFIX = `$temp-`;

export function isPersistedCustomProperty(customPropertyId) {
  return customPropertyId && !customPropertyId.toString().startsWith(INLINE_CUSTOM_PROPERTY_ID_PREFIX);
}

export function isInlineCustomProperty(customPropertyId) {
  return customPropertyId && customPropertyId.toString().startsWith(INLINE_CUSTOM_PROPERTY_ID_PREFIX);
}

export function isBehavioralCustomProperty(property: CustomProperty) {
  return !!getOwnAttr(property, `behavior`);
}

export function getInlineCustomPropertyId(customPropertyId) {
  return customPropertyId || `${INLINE_CUSTOM_PROPERTY_ID_PREFIX}${nanoid()}`;
}

export function canUseBorrowedProperties(project: CurrentProject | ProjectDetails | null): boolean | undefined {
  return project?.hasFeatureFlag(ProjectFeatureFlag.BorrowedProperties);
}

export function getBorrowedProperty(customProperty?: CustomProperty): Property | null {
  if (!customProperty) {
    return null;
  }
  return Object.values(customProperty.composedProperties || {}).find((prop: Property) => `borrowEvent` in prop);
}

/**
 * Checks if a custom property is itself a borrowed property or references a nested custom property that is a
 * borrowed property
 */
export function isBorrowedPropertyReferenced(customProperty: CustomProperty): boolean {
  if (!customProperty.customPropertyId) {
    // // inline custom property
    return Object.values(customProperty.composedProperties).some((prop: Property) => {
      // check if inline custom property references a saved custom property that references a borrowed prop
      return Boolean(prop.customProperty?.referencesBorrowedProperty);
    });
  } else {
    return customProperty.referencesBorrowedProperty;
  }
}

export function trackingDataForBorrowedProperty(customProperty?: CustomProperty) {
  const borrowedProperty = getBorrowedProperty(customProperty);
  if (!borrowedProperty) {
    return null;
  }

  return {
    name: customProperty.name,
    customPropertyId: customProperty.customPropertyId,
    borrowEvent: borrowedProperty.borrowEvent.value,
    borrowProperty: borrowedProperty.value,
  };
}
